import { AutoAwesome, AutoStories, ChevronRight, Fullscreen, FullscreenExit, History, Save, Tune, MenuBook, MoreVert } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/page-header';
import { useSubscription } from '@/hooks/useSubscription';
import { WriterRoomStatus } from '@/lib/types';

interface EditorHeaderProps {
  novelName: string;
  textTitle: string;
  status: WriterRoomStatus;

  isAutosaving: boolean;
  autosaveEnabled: boolean;
  onAutosaveToggle: (enabled: boolean) => void;
  onSave: () => void;
  canSave: boolean;
  isHistoryDrawerOpen: boolean;
  isEntitiesDrawerOpen: boolean;
  isScenePropertiesOpen: boolean;
  isOutlineDrawerOpen: boolean;
  onToggleHistoryDrawer: () => void;
  onToggleEntitiesDrawer: () => void;
  onToggleSceneProperties: () => void;
  onToggleOutlineDrawer: () => void;
  isWritersRoomOpen: boolean;
  onToggleWritersRoom: () => void;
  canUseAutosave?: boolean;
  canUseWritersRoom?: boolean;

  isFocusMode: boolean;
  onToggleFocusMode: () => void;
  onChangeStatus?: (phase: string) => void;
}

export function EditorHeader({
  novelName,
  textTitle,
  status,
  onSave,
  canSave,
  isAutosaving,
  isHistoryDrawerOpen,
  onToggleHistoryDrawer,
  isFocusMode,
  onToggleFocusMode,
  isScenePropertiesOpen,
  onToggleSceneProperties,
  isEntitiesDrawerOpen,
  onToggleEntitiesDrawer,
  isOutlineDrawerOpen,
  onToggleOutlineDrawer,
  isWritersRoomOpen,
  onToggleWritersRoom,
  autosaveEnabled,
  onAutosaveToggle,
  canUseAutosave = false,
  canUseWritersRoom = false,
  onChangeStatus,
}: EditorHeaderProps) {
  const { t } = useTranslation();
  const { hasFeature } = useSubscription();
  const isGenerativeAiAllowed = hasFeature('is_generative_ai_allowed');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileAnchorEl, setMobileAnchorEl] = useState<null | HTMLElement>(null);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileAnchorEl(event.currentTarget);
  };
  const handleMobileMenuClose = () => {
    setMobileAnchorEl(null);
  };

  const saveButton = (
    <Button
      id="editor-save-btn"
      size="small"
      variant="outlined"
      color="primary"
      onClick={onSave}
      disabled={!canSave || isAutosaving}
      startIcon={isAutosaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 2,
        ml: 1,
        px: 2,
        borderWidth: 2,
        '&:hover': { borderWidth: 2 }
      }}
    >
      {isAutosaving ? t('editor.saving') : t('common.save')}
    </Button>
  );

  return (
    <PageHeader
      title={textTitle}
      breadcrumbs={[{ label: novelName }]}
      leadingAction={!isMobile && !isOutlineDrawerOpen ? (
        <Tooltip title={t('editor.outline')}>
          <IconButton onClick={onToggleOutlineDrawer} size="small" color="default">
            <ChevronRight />
          </IconButton>
        </Tooltip>
      ) : undefined}
      sx={{
        px: { xs: 2, sm: 3 },
        py: 1.5,
        mb: 0,
        flexDirection: 'row',
        alignItems: { xs: 'flex-start', sm: 'center' },
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        zIndex: 1100,
        '& h1': { fontSize: { xs: '1.25rem', sm: '1.5rem' }, m: 0, mt: 0.5 },
        '& .page-header-content': {
          flexGrow: 1,
          minWidth: 0,
          mr: 1
        }
      }}
      actions={
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {!isMobile && (
            <Select
              value={status?.phase || 'idle'}
              onChange={(e) => onChangeStatus?.(e.target.value as string)}
              renderValue={(selected) => t(`writers_room.phases.${selected}`, selected as string)}
              size="small"
              variant="standard"
              disableUnderline
              sx={{
                height: 22,
                fontSize: 10,
                fontWeight: 700,
                bgcolor: 'warning.main',
                color: 'warning.contrastText',
                borderRadius: 1,
                mr: 2,
                px: 1,
                '& .MuiSelect-select': {
                  py: 0.5,
                  px: 0.5,
                  textTransform: 'uppercase',
                },
                '& .MuiSvgIcon-root': {
                  color: 'warning.contrastText',
                  fontSize: 14,
                }
              }}
            >
              <MenuItem value="idle"><Typography variant="caption" sx={{fontWeight:700, fontSize:10, textTransform:'uppercase'}}>{t('writers_room.phases.idle')}</Typography></MenuItem>
              {isGenerativeAiAllowed && <MenuItem value="snapshot"><Typography variant="caption" sx={{fontWeight:700, fontSize:10, textTransform:'uppercase'}}>{t('writers_room.phases.snapshot')}</Typography></MenuItem>}
              {isGenerativeAiAllowed && <MenuItem value="prepared"><Typography variant="caption" sx={{fontWeight:700, fontSize:10, textTransform:'uppercase'}}>{t('writers_room.phases.prepared')}</Typography></MenuItem>}
              <MenuItem value="writing"><Typography variant="caption" sx={{fontWeight:700, fontSize:10, textTransform:'uppercase'}}>{t('writers_room.phases.writing')}</Typography></MenuItem>
              <MenuItem value="editing"><Typography variant="caption" sx={{fontWeight:700, fontSize:10, textTransform:'uppercase'}}>{t('writers_room.phases.editing')}</Typography></MenuItem>
              {isGenerativeAiAllowed && <MenuItem value="validating"><Typography variant="caption" sx={{fontWeight:700, fontSize:10, textTransform:'uppercase'}}>{t('writers_room.phases.validating')}</Typography></MenuItem>}
              <MenuItem value="approved"><Typography variant="caption" sx={{fontWeight:700, fontSize:10, textTransform:'uppercase'}}>{t('writers_room.phases.approved')}</Typography></MenuItem>
              <MenuItem value="needs_fixes"><Typography variant="caption" sx={{fontWeight:700, fontSize:10, textTransform:'uppercase'}}>{t('writers_room.phases.needs_fixes')}</Typography></MenuItem>
              {isGenerativeAiAllowed && <MenuItem value="auto_fixing"><Typography variant="caption" sx={{fontWeight:700, fontSize:10, textTransform:'uppercase'}}>{t('writers_room.phases.auto_fixing')}</Typography></MenuItem>}
              <MenuItem value="ready"><Typography variant="caption" sx={{fontWeight:700, fontSize:10, textTransform:'uppercase'}}>{t('writers_room.phases.ready')}</Typography></MenuItem>
            </Select>
          )}

          {isMobile ? (
            <>
              {saveButton}
              <IconButton onClick={handleMobileMenuOpen} color="inherit">
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={mobileAnchorEl}
                open={Boolean(mobileAnchorEl)}
                onClose={handleMobileMenuClose}
              >
                <MenuItem onClick={() => { onToggleOutlineDrawer(); handleMobileMenuClose(); }}>
                  <ListItemIcon>
                    <MenuBook fontSize="small" color={isOutlineDrawerOpen ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText>{t('editor.outline')}</ListItemText>
                </MenuItem>
                {canUseWritersRoom && (
                  <MenuItem onClick={() => { onToggleWritersRoom(); handleMobileMenuClose(); }}>
                    <ListItemIcon>
                      <AutoAwesome fontSize="small" color={isWritersRoomOpen ? "primary" : "inherit"} />
                    </ListItemIcon>
                    <ListItemText>{t('writers_room.title')}</ListItemText>
                  </MenuItem>
                )}
                <MenuItem onClick={() => { onToggleSceneProperties(); handleMobileMenuClose(); }}>
                  <ListItemIcon>
                    <Tune fontSize="small" color={isScenePropertiesOpen ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText>{t('editor.scene_properties')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { onToggleHistoryDrawer(); handleMobileMenuClose(); }}>
                  <ListItemIcon>
                    <History fontSize="small" color={isHistoryDrawerOpen ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText>{t('editor.version_history')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { onToggleEntitiesDrawer(); handleMobileMenuClose(); }}>
                  <ListItemIcon>
                    <AutoStories fontSize="small" color={isEntitiesDrawerOpen ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText>{t('editor.entities')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={(e) => { e.preventDefault(); }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={autosaveEnabled}
                        onChange={(e) => onAutosaveToggle(e.target.checked)}
                        disabled={!canUseAutosave}
                        size="small"
                      />
                    }
                    label={t('editor.autosave')}
                    sx={{ margin: 0 }}
                  />
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              {canUseWritersRoom && (
                <Tooltip title={t('writers_room.title')}>
                  <IconButton onClick={onToggleWritersRoom} color={isWritersRoomOpen ? "primary" : "default"} size="small">
                    <AutoAwesome />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={t('editor.scene_properties')}>
                <IconButton id="editor-properties-btn" onClick={onToggleSceneProperties} color={isScenePropertiesOpen ? "primary" : "default"} size="small">
                  <Tune />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('editor.version_history')}>
                <IconButton onClick={onToggleHistoryDrawer} color={isHistoryDrawerOpen ? "primary" : "default"} size="small">
                  <History />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('editor.entities')}>
                <IconButton id="editor-entities-btn" onClick={onToggleEntitiesDrawer} color={isEntitiesDrawerOpen ? "primary" : "default"} size="small">
                  <AutoStories />
                </IconButton>
              </Tooltip>

              <Tooltip title={canUseAutosave ? "" : t('editor.autosave_available_pro')}>
                <span>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={autosaveEnabled}
                        onChange={(e) => onAutosaveToggle(e.target.checked)}
                        disabled={!canUseAutosave}
                        size="small"
                      />
                    }
                    label={t('editor.autosave')}
                    sx={{
                      margin: 0,
                      mr: 1,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: canUseAutosave ? 'text.secondary' : 'text.disabled',
                      },
                    }}
                  />
                </span>
              </Tooltip>

              {saveButton}

              <Button
                variant="contained"
                color="primary"
                startIcon={isFocusMode ? <FullscreenExit /> : <Fullscreen />}
                onClick={onToggleFocusMode}
                sx={{
                  ml: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                {isFocusMode ? t('editor.exit_focus') : t('editor.focus_mode')}
              </Button>
            </>
          )}
        </Box>
      }
    />
  );
}
